import {
  Box,
  CircularProgress,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import clsx from 'clsx';
import { differenceInCalendarDays } from 'date-fns';
import {
  LeftPanelCloseIcon,
  LeftPanelOpenIcon,
  WbAvatar,
  WbBadge,
  WbButton,
  WbCardHeader,
  WbDivider,
  WbDrawer,
  WbDrawerContent,
  WbFieldLabel,
  WbIcon,
  WbTooltip,
  useEntitiesByRefs,
  useIdentitiesDisplayNames,
} from '@agilelab/plugin-wb-platform';
import { entityToUrn } from '@agilelab/plugin-wb-builder-common';
import { expandEntityRefSafe } from '@agilelab/plugin-wb-platform-common';
import ArchiveIcon from '@material-ui/icons/Archive';
import BuildIcon from '@material-ui/icons/Build';
import CheckIcon from '@material-ui/icons/Check';
import CloudDoneIcon from '@material-ui/icons/CloudDone';
import CloseIcon from '@material-ui/icons/Close';
import CodeIcon from '@material-ui/icons/Code';
import EditIcon from '@material-ui/icons/Edit';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CancelIcon from '@material-ui/icons/Cancel';
import SettingsIcon from '@material-ui/icons/Settings';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';
import VisibilityIcon from '@material-ui/icons/Visibility';
import WarningIcon from '@material-ui/icons/Warning';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import RocketLaunchIcon from '@material-ui/icons/FlightTakeoff';
import HistoryIcon from '@material-ui/icons/History';
import PendingIcon from '@material-ui/icons/Schedule';
import { useApi } from '@mainstage/core-plugin-api';
import { useEntity } from '@mainstage/plugin-catalog-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  type ComponentType,
  type CSSProperties,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { systemLifecycleApiRef, SystemLifecycleProjection } from '../../api';
import { WorkflowDefinitionGraph } from './WorkflowDefinitionGraph';
import { getLifecycleMacroPhaseColor } from './lifecycleMacroPhaseColors';

export { WorkflowDefinitionGraph } from './WorkflowDefinitionGraph';

/** ─── Icon Map ─── */

const iconMap: Record<string, ComponentType<{ style?: CSSProperties }>> = {
  Archive: ArchiveIcon,
  Build: BuildIcon,
  check: CheckIcon,
  CloudDone: CloudDoneIcon,
  close: CloseIcon,
  code: CodeIcon,
  edit: EditIcon,
  equalizer: EqualizerIcon,
  expand_more: ExpandMoreIcon,
  cancel: CancelIcon,
  settings: SettingsIcon,
  Settings: SettingsIcon,
  remove_red_eye: VisibilityIcon,
  warning: WarningIcon,
  savings: AccountBalanceWalletIcon,
  rocket_launch: RocketLaunchIcon,
  history: HistoryIcon,
  pending: PendingIcon,
};

function MIcon({ name, style }: { name: string; style?: CSSProperties }) {
  const IconComponent = iconMap[name];
  if (name.startsWith('mui:')) return <WbIcon icon={name} style={style} />;
  if (!IconComponent) return <span style={style}>{name}</span>;
  return <IconComponent style={style} />;
}

/** ─── Types ─── */

export interface LifecycleStage {
  id: string;
  label: string;
  description?: string;
  macroPhase?: string;
  icon: string;
  status: 'completed' | 'current' | 'next' | 'future' | 'pending';
}

export interface Prerequisite {
  label: string;
  status: 'success' | 'error' | 'warning';
  icon: string;
}

export interface HistoryEvent {
  id: string;
  date: string;
  title: string;
  description: string;
  actor: string;
  avatarDisplayName: string;
  transitionId?: string;
  type: 'trigger' | 'precondition';
  outcome: 'completed' | 'rejected' | 'failed';
  avatarUrl?: string;
  icon?: string | null;
  iconColor?: string;
}

export interface LifecycleAction {
  id: string;
  label: string;
  icon: string;
  variant: 'primary' | 'secondary' | 'tertiary';
}

export interface LifecycleData {
  currentState: string;
  visitedStages: string[];
  macroPhases: LifecycleStage[];
  stages: LifecycleStage[];
  transitions: Array<{
    id: string;
    fromStage: string | null;
    toStage: string;
    label?: string;
    when?: Record<string, unknown>;
    show?: boolean;
    featured?: boolean;
    requirements: Array<{
      type: 'trigger' | 'precondition';
      label: string;
      satisfied?: boolean;
    }>;
  }>;
  actions: LifecycleAction[];
}

function macroPhaseStatus(
  phaseIndex: number,
  currentPhaseIndex: number,
): LifecycleStage['status'] {
  if (phaseIndex < currentPhaseIndex) return 'completed';
  if (phaseIndex === currentPhaseIndex) return 'current';
  return 'future';
}

function toLifecycleData(
  projection: Extract<SystemLifecycleProjection, { available: true }>,
): LifecycleData {
  const currentPhase = projection.stages.find(
    stage => stage.id === projection.currentStage,
  )?.macroPhase;
  const currentPhaseIndex = projection.macroPhases.findIndex(
    phase => phase.id === currentPhase,
  );
  return {
    currentState: projection.currentStage,
    visitedStages: [],
    macroPhases: projection.macroPhases.map((phase, index) => ({
      id: phase.id,
      label: phase.label ?? phase.id,
      icon: phase.icon ?? 'equalizer',
      status: macroPhaseStatus(index, currentPhaseIndex),
    })),
    stages: projection.stages.map(stage => ({
      id: stage.id,
      label: stage.label,
      description: stage.description,
      macroPhase: stage.macroPhase,
      icon: stage.icon ?? 'settings',
      status: stage.id === projection.currentStage ? 'current' : 'future',
    })),
    transitions: projection.transitions.map((transition, index) => ({
      id:
        transition.id ??
        `${transition.fromStage ?? 'start'}-${transition.toStage}-${index}`,
      fromStage: transition.fromStage,
      toStage: transition.toStage,
      label: transition.label,
      when: transition.when,
      show: transition.show,
      featured: transition.featured,
      requirements: transition.requirements,
    })),
    actions: [],
  };
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0,
    height: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  workflowArea: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    padding: theme.spacing(0.5, 1.5, 1.5),
  },
  workflowChart: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
    border: `1px solid ${theme.palette.blueGrey[100]}`,
    borderRadius: theme.spacing(0.75),
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  chartHeader: {
    backgroundColor: theme.palette.white,
  },
  chartContent: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
  },
  sidePanel: {
    flex: '0 0 400px',
    maxWidth: '45%',
    minWidth: 0,
    borderLeft: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  collapsedSidePanel: {
    width: 36,
    flexShrink: 0,
    borderLeft: `1px solid ${theme.palette.grey[200]}`,
    paddingTop: 6,
  },
  infoPanelToggle: {
    marginLeft: theme.spacing(1),
  },
  infoPanelHeaderContent: {
    paddingLeft: theme.spacing(0.25),
  },
  headerText: {
    fontSize: 14,
  },
  cardHeaderText: {
    fontSize: 12,
  },
  transitionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 0,
  },
  infoField: {
    marginTop: theme.spacing(2),
  },
  infoSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
  sectionTitle: {
    ...theme.typography.subtitle2,
    fontSize: 14,
    fontWeight: 400,
    marginBottom: theme.spacing(2),
  },
  sidePanelContent: { flex: 1, overflow: 'auto', padding: 12 },
  mainContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    gap: 12,
    minHeight: 48,
    flexShrink: 0,
    flexWrap: 'wrap',
    boxSizing: 'border-box',
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginLeft: 'auto',
  },
  stagesContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    height: 98,
    boxSizing: 'border-box',
    flexShrink: 0,
    padding: theme.spacing(1.5, 1.5, 1),
    gap: theme.spacing(1),
    overflowX: 'auto',
    overflowY: 'hidden',
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
  stage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: '1 0 0',
    minWidth: 120,
    padding: theme.spacing(1, 0, 2),
  },
  stageLabel: {
    ...theme.typography.body1,
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.text.primary,
    height: 21,
    textTransform: 'uppercase',
  },
  inactiveStage: {
    opacity: 0.5,
  },
  stageIconFrame: {
    width: 32,
    height: 32,
    boxSizing: 'border-box',
    border: '1px solid',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0px 2px 4px 0px rgba(146,146,146,0.25)',
  },
  stageLine: {
    flex: 1,
    borderTop: `1px dashed ${theme.palette.blueGrey[200]}`,
    minWidth: 32,
    marginTop: 39,
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: -3,
      width: 4,
      height: 4,
      borderTop: '1px solid',
      borderRight: '1px solid',
      borderColor: theme.palette.blueGrey[200],
      transform: 'rotate(45deg)',
    },
  },
  currentStateContent: {
    flex: 1,
    position: 'relative',
    padding: 0,
    minHeight: 320,
    backgroundColor: theme.palette.background.paper,
  },
  historyContent: {
    flex: 1,
    overflow: 'auto',
    padding: 12,
  },
  historyDrawer: {
    '&&': {
      width: 576,
      maxWidth: '100%',
    },
    '& [class*="WbDrawer-header-"]': {
      height: 48,
      boxSizing: 'border-box',
    },
    '& [class*="WbDrawer-headerContent-"]': {
      height: '100%',
      padding: theme.spacing(0, 1.5),
    },
  },
  historyDrawerTitle: {
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 0,
  },
  historyMetadata: {
    fontSize: 11,
  },
  historyGroup: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.secondary.dark,
    marginBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    '&:first-child': {
      paddingTop: 0,
      borderTop: 'none',
    },
  },
  historyEvent: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  historyAvatar: {
    width: 32,
    height: 32,
    flexShrink: 0,
  },
  historyEventContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    flex: 1,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  historyEventTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  historyEventDetails: {
    paddingBottom: theme.spacing(1),
  },
  historyTitle: {
    fontWeight: 700,
  },
  prerequisiteItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 34,
    boxSizing: 'border-box',
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.spacing(0.5),
    marginTop: 6,
  },
  prereqSuccess: {
    backgroundColor: theme.palette.bkg.success,
  },
  prereqError: {
    backgroundColor: theme.palette.bkg.error,
  },
  prereqWarning: {
    backgroundColor: theme.palette.bkg.warning,
  },
  actionButton: {
    height: 32,
  },
  transitionButton: {
    width: '100%',
    justifyContent: 'flex-start',
    whiteSpace: 'normal',
    textAlign: 'left',
    '&&': {
      border: 'none',
      borderRadius: theme.spacing(0.5),
      backgroundColor: theme.palette.bkg.primary,
      color: theme.palette.text.primary,
      padding: theme.spacing(1, 1.5),
    },
    '&&:hover': {
      backgroundColor: theme.palette.softened.primary,
    },
  },
  transitionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& > svg': {
      fontSize: 16,
      flexShrink: 0,
      color: theme.palette.primary.main,
    },
  },
}));

/** ─── Sub-components ─── */

function StageIndicator({
  stage,
  index,
}: {
  stage: LifecycleStage;
  index: number;
}) {
  const classes = useStyles();
  const theme = useTheme();
  const color = getLifecycleMacroPhaseColor(index);

  return (
    <Box
      className={clsx(
        classes.stage,
        stage.status !== 'current' && classes.inactiveStage,
      )}
    >
      <Typography noWrap className={classes.stageLabel}>
        {stage.label}
      </Typography>
      <Box
        className={classes.stageIconFrame}
        style={{
          backgroundColor: theme.palette.bkg[color],
          borderColor: theme.palette[color].dark,
        }}
      >
        <MIcon
          name={stage.icon}
          style={{
            fontSize: 24,
            color: theme.palette[color].dark,
          }}
        />
      </Box>
    </Box>
  );
}

function StageLine() {
  const classes = useStyles();
  return <Box className={classes.stageLine} />;
}

function PrerequisiteItem({ item }: { item: Prerequisite }) {
  const classes = useStyles();
  const theme = useTheme();
  const bgClassMap: Record<string, string> = {
    success: classes.prereqSuccess,
    error: classes.prereqError,
  };
  const bgClass = bgClassMap[item.status] ?? classes.prereqWarning;

  const iconColorMap: Record<string, string> = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
  };
  const iconColor = iconColorMap[item.status] ?? theme.palette.warning.main;

  return (
    <Box className={`${classes.prerequisiteItem} ${bgClass}`}>
      <MIcon name={item.icon} style={{ fontSize: 16, color: iconColor }} />
      <Typography variant="body1">{item.label}</Typography>
    </Box>
  );
}

function HistoryEventItem({ event }: { event: HistoryEvent }) {
  const classes = useStyles();
  const theme = useTheme();
  const iconColorMap: Record<string, string> = {
    error: theme.palette.error.main,
    primary: theme.palette.primary.main,
  };
  const iconColor =
    iconColorMap[event.iconColor ?? ''] ?? theme.palette.secondary.main;
  return (
    <Box className={classes.historyEvent}>
      <WbAvatar
        displayName={event.avatarDisplayName}
        picture={event.avatarUrl || undefined}
        className={classes.historyAvatar}
      />
      <Box className={classes.historyEventContent}>
        <Typography variant="body2" className={classes.historyMetadata}>
          {event.date}
        </Typography>
        <Box className={classes.historyEventDetails}>
          <Box className={classes.historyEventTitle}>
            {event.icon && (
              <MIcon
                name={event.icon}
                style={{
                  fontSize: 16,
                  color: iconColor,
                }}
              />
            )}
            <Typography
              variant="body1"
              color="textSecondary"
              className={classes.historyTitle}
            >
              {event.title}
            </Typography>
          </Box>
          <Typography variant="body1">{event.description}</Typography>
        </Box>
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.historyMetadata}
        >
          Performed by {event.actor}
        </Typography>
      </Box>
    </Box>
  );
}

const HISTORY_PAGE_SIZE = 20;

function historyDateGroup(timestamp: string): string {
  const date = new Date(timestamp);
  const daysAgo = differenceInCalendarDays(new Date(), date);
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  if (daysAgo >= 2 && daysAgo <= 7) return 'Last week';
  return date.toLocaleDateString();
}

export function LifecycleWorkflowView({
  data,
  systemUrn,
}: {
  data: LifecycleData;
  systemUrn?: string;
}) {
  const classes = useStyles();
  const systemLifecycleApi = useApi(systemLifecycleApiRef);
  const [selectedStageId, setSelectedStageId] = useState(data.currentState);
  const [selectedTransitionId, setSelectedTransitionId] = useState<string>();
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(true);
  const historyLoadMoreRef = useRef<HTMLDivElement>(null);
  const {
    data: historyPages,
    error: historyError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isHistoryLoading,
  } = useInfiniteQuery({
    queryKey: ['system-lifecycle-history', systemUrn],
    queryFn: ({ pageParam = 0 }) => {
      if (!systemUrn) {
        return Promise.resolve({
          items: [],
          total: 0,
          offset: 0,
          limit: HISTORY_PAGE_SIZE,
          hasMore: false,
        });
      }
      return systemLifecycleApi.getHistory(
        systemUrn,
        pageParam as number,
        HISTORY_PAGE_SIZE,
      );
    },
    getNextPageParam: lastPage =>
      lastPage.hasMore ? lastPage.offset + lastPage.items.length : undefined,
    enabled: isHistoryDrawerOpen && Boolean(systemUrn),
  });
  useEffect(() => {
    const target = historyLoadMoreRef.current;
    if (!target || !hasNextPage || isFetchingNextPage) return () => {};
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) void fetchNextPage();
      },
      { rootMargin: '160px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  useEffect(() => {
    setSelectedStageId(data.currentState);
    setSelectedTransitionId(undefined);
  }, [data.currentState]);
  const selectedStage =
    data.stages.find(stage => stage.id === selectedStageId) ??
    data.stages.find(stage => stage.id === data.currentState);
  const selectedStageTransitions = selectedStage
    ? data.transitions.filter(
        transition =>
          transition.fromStage === selectedStage.id &&
          transition.show !== false,
      )
    : [];
  const selectedTransition = data.transitions.find(
    transition => transition.id === selectedTransitionId,
  );
  const selectedTrigger = selectedTransition?.requirements.find(
    requirement => requirement.type === 'trigger',
  );
  const selectedPreconditions =
    selectedTransition?.requirements
      .filter(requirement => requirement.type === 'precondition')
      .map(requirement => {
        if (requirement.satisfied) {
          return {
            label: requirement.label,
            status: 'success' as const,
            icon: 'check',
          };
        }
        return {
          label: requirement.label,
          status: 'warning' as const,
          icon: 'warning',
        };
      }) ?? [];
  const currentMacroPhase = data.macroPhases.find(
    macroPhase => macroPhase.status === 'current',
  )?.label;
  const stageLabelById = new Map(
    data.stages.map(stage => [stage.id, stage.label]),
  );
  const historyEvents = useMemo(
    () =>
      (historyPages?.pages ?? []).flatMap(page =>
        page.items.map(event => ({
          id: event.id,
          date: new Date(event.timestamp).toLocaleString(),
          dateGroup: historyDateGroup(event.timestamp),
          title: event.label,
          description:
            event.reason ??
            (event.type === 'precondition'
              ? 'Precondition evaluated.'
              : 'Trigger received.'),
          actor: event.actor,
          avatarDisplayName: event.actor,
          transitionId: event.transitionId,
          type: event.type,
          outcome: event.outcome,
          icon: event.outcome === 'completed' ? 'check' : 'warning',
          iconColor: event.outcome === 'completed' ? 'primary' : 'error',
        })),
      ),
    [historyPages],
  );
  const historyActors = useMemo(
    () => [
      ...new Set(
        historyEvents
          .map(event => event.actor)
          .filter(actor => actor.startsWith('user:')),
      ),
    ],
    [historyEvents],
  );
  const { data: actorDisplayNames } = useIdentitiesDisplayNames(historyActors);
  const historyActorRefs = useMemo(
    () => historyActors.map(actor => expandEntityRefSafe(actor) ?? actor),
    [historyActors],
  );
  const { data: actorEntities } = useEntitiesByRefs(historyActorRefs, [
    'metadata.name',
    'spec.profile',
  ]);
  const actorProfiles = new Map<
    string,
    { displayName: string; picture?: string }
  >(
    (actorEntities?.items ?? []).flatMap((entity, index) => {
      const actorRef = historyActorRefs[index];
      if (!entity || !actorRef) return [];
      const profile = entity.spec?.profile;
      const profileValues =
        profile && typeof profile === 'object' && !Array.isArray(profile)
          ? profile
          : {};
      const displayName = profileValues.displayName;
      const picture = profileValues.picture;
      return [
        [
          actorRef,
          {
            displayName:
              typeof displayName === 'string'
                ? displayName
                : entity.metadata.name,
            ...(typeof picture === 'string' ? { picture } : {}),
          },
        ],
      ];
    }),
  );
  const history = historyEvents.map(event => {
    const profile = actorProfiles.get(
      expandEntityRefSafe(event.actor) ?? event.actor,
    );
    return {
      ...event,
      actor:
        profile?.displayName ??
        actorDisplayNames?.get(event.actor) ??
        event.actor,
      avatarDisplayName:
        profile?.displayName ??
        actorDisplayNames?.get(event.actor) ??
        event.actor,
      avatarUrl: profile?.picture,
    };
  });
  const selectedTransitionTitle = selectedTransition
    ? selectedTransition.label ??
      `${
        stageLabelById.get(selectedTransition.fromStage ?? '') ?? 'Start'
      } to ${
        stageLabelById.get(selectedTransition.toStage) ??
        selectedTransition.toStage
      }`
    : undefined;
  const selectedTransitionDirection = selectedTransition
    ? `${stageLabelById.get(selectedTransition.fromStage ?? '') ?? 'Start'} → ${
        stageLabelById.get(selectedTransition.toStage) ??
        selectedTransition.toStage
      }`
    : undefined;
  const selectedStageMacroPhase = data.macroPhases.find(
    macroPhase => macroPhase.id === selectedStage?.macroPhase,
  )?.label;

  const selectStage = (stageId: string) => {
    setSelectedStageId(stageId);
    setSelectedTransitionId(undefined);
  };
  const selectTransition = (transitionId: string) => {
    setSelectedTransitionId(transitionId);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.stagesContainer}>
        {data.macroPhases.map((macroPhase, index) => (
          <Fragment key={macroPhase.id}>
            <StageIndicator stage={macroPhase} index={index} />
            {index < data.macroPhases.length - 1 && <StageLine />}
          </Fragment>
        ))}
      </Box>
      <Box className={classes.toolbar}>
        <Typography variant="subtitle2" className={classes.headerText}>
          Lifecycle stage: <strong>{currentMacroPhase ?? 'Unknown'}</strong>
          {' | '}Workflow stage:{' '}
          <strong>
            {stageLabelById.get(data.currentState) ?? data.currentState}
          </strong>
        </Typography>
        <Box className={classes.toolbarActions}>
          {systemUrn && (
            <WbButton
              className={classes.actionButton}
              icon={<HistoryIcon />}
              label="Show History"
              variant="outlined"
              onClick={() => setIsHistoryDrawerOpen(true)}
            />
          )}
          {data.actions.length > 0 && (
            <WbDivider orientation="vertical" height="24px" />
          )}
          {data.actions.map(action => (
            <WbButton
              key={action.id}
              className={classes.actionButton}
              icon={<MIcon name={action.icon} />}
              label={action.label}
              variant="outlined"
              disabled
            />
          ))}
        </Box>
      </Box>
      <Box className={classes.workflowArea}>
        <Box className={classes.workflowChart}>
          <WbCardHeader
            title="Workflow chart"
            className={classes.chartHeader}
            typographyProps={{
              variant: 'subtitle2',
              className: classes.cardHeaderText,
            }}
          />
          <Box className={classes.chartContent}>
            <Box className={classes.mainContent}>
              <Box className={classes.currentStateContent}>
                <WorkflowDefinitionGraph
                  stages={data.stages}
                  macroPhases={data.macroPhases}
                  transitions={data.transitions}
                  currentStage={data.currentState}
                  visitedStages={new Set(data.visitedStages)}
                  selectedStageId={
                    selectedTransition ? undefined : selectedStage?.id
                  }
                  selectedTransitionId={selectedTransitionId}
                  onStageSelect={selectStage}
                  onTransitionSelect={selectTransition}
                />
              </Box>
            </Box>
            {isInfoPanelOpen && (
              <Box className={classes.sidePanel}>
                <WbCardHeader
                  title="Info panel"
                  contentClassname={classes.infoPanelHeaderContent}
                  typographyProps={{
                    variant: 'subtitle2',
                    className: classes.cardHeaderText,
                  }}
                  leading={
                    <WbTooltip title="Collapse info panel">
                      <WbButton
                        icon={
                          <LeftPanelCloseIcon
                            fontSize="small"
                            style={{ transform: 'rotate(180deg)' }}
                          />
                        }
                        aria-label="Collapse info panel"
                        className={classes.infoPanelToggle}
                        aria-expanded={isInfoPanelOpen}
                        variant="text"
                        color="primary"
                        style={{ minWidth: 24, minHeight: 24, padding: 4 }}
                        onClick={() => setIsInfoPanelOpen(false)}
                      />
                    </WbTooltip>
                  }
                />
                <Box className={classes.sidePanelContent}>
                  {selectedTransition && (
                    <>
                      <Box className={classes.transitionTitle}>
                        <SubdirectoryArrowRightIcon
                          style={{ fontSize: 16, color: '#4e6980' }}
                        />
                        <Typography variant="h5" className={classes.infoTitle}>
                          {selectedTransitionTitle}
                        </Typography>
                      </Box>
                      <WbBadge variant="stroke" color="accent">
                        Transition
                      </WbBadge>
                      <Box className={classes.infoField}>
                        <WbFieldLabel label="Direction" />
                        <Typography variant="body1">
                          {selectedTransitionDirection}
                        </Typography>
                      </Box>
                      {selectedTrigger && (
                        <Box className={classes.infoField}>
                          <WbFieldLabel label="Trigger type" />
                          <WbBadge variant="stroke" color="primary">
                            {selectedTrigger.label}
                          </WbBadge>
                        </Box>
                      )}
                    </>
                  )}
                  {selectedPreconditions.length > 0 && (
                    <Box className={classes.infoSection}>
                      <Typography className={classes.sectionTitle}>
                        Preconditions
                      </Typography>
                      {selectedPreconditions.map((precondition, index) => (
                        <PrerequisiteItem key={index} item={precondition} />
                      ))}
                    </Box>
                  )}
                  {!selectedTransition && selectedStage && (
                    <>
                      <Box className={classes.transitionTitle}>
                        <MIcon
                          name={selectedStage.icon}
                          style={{ fontSize: 18, color: '#4e6980' }}
                        />
                        <Typography variant="h5" className={classes.infoTitle}>
                          {selectedStage.label}
                        </Typography>
                      </Box>
                      <WbBadge variant="stroke" color="accent">
                        State
                      </WbBadge>
                      {selectedStage.description && (
                        <Box className={classes.infoField}>
                          <WbFieldLabel label="Description" />
                          <Typography variant="body1">
                            {selectedStage.description}
                          </Typography>
                        </Box>
                      )}
                      <Box className={classes.infoField}>
                        <WbFieldLabel label="Macro phase" />
                        <Typography variant="body1">
                          {selectedStageMacroPhase ?? '-'}
                        </Typography>
                      </Box>
                      <Box className={classes.infoSection}>
                        <Typography className={classes.sectionTitle}>
                          Possible transitions
                        </Typography>
                        {selectedStageTransitions.length > 0 ? (
                          selectedStageTransitions.map(transition => (
                            <WbButton
                              key={transition.id}
                              fullWidth
                              variant="outlined"
                              className={clsx(
                                classes.prerequisiteItem,
                                classes.transitionButton,
                              )}
                              label={
                                <Box className={classes.transitionLabel}>
                                  <SubdirectoryArrowRightIcon />
                                  <Typography variant="body1">
                                    {transition.label ??
                                      `${selectedStage.label} to ${
                                        stageLabelById.get(
                                          transition.toStage,
                                        ) ?? transition.toStage
                                      }`}
                                  </Typography>
                                </Box>
                              }
                              onClick={() => selectTransition(transition.id)}
                            />
                          ))
                        ) : (
                          <Typography variant="body1">
                            No outgoing transitions.
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}
            {!isInfoPanelOpen && (
              <Box className={classes.collapsedSidePanel}>
                <WbTooltip title="Expand info panel">
                  <WbButton
                    icon={
                      <LeftPanelOpenIcon
                        fontSize="small"
                        style={{ transform: 'rotate(180deg)' }}
                      />
                    }
                    aria-label="Expand info panel"
                    aria-expanded={isInfoPanelOpen}
                    variant="text"
                    color="primary"
                    style={{ minWidth: 24, minHeight: 24, padding: 4 }}
                    onClick={() => setIsInfoPanelOpen(true)}
                  />
                </WbTooltip>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <WbDrawer
        open={isHistoryDrawerOpen}
        setOpen={() => setIsHistoryDrawerOpen(false)}
        title={
          <Typography
            variant="subtitle2"
            component="h2"
            className={classes.historyDrawerTitle}
          >
            History
          </Typography>
        }
        PaperProps={{ className: classes.historyDrawer }}
      >
        <WbDrawerContent className={classes.historyContent}>
          {isHistoryLoading && <CircularProgress size={24} />}
          {historyError ? (
            <Typography>Unable to load lifecycle history.</Typography>
          ) : null}
          {!isHistoryLoading && !historyError && history.length === 0 && (
            <Typography>No lifecycle history is available.</Typography>
          )}
          {history.map((event, index) => (
            <Fragment key={event.id}>
              {event.dateGroup !== history[index - 1]?.dateGroup && (
                <Typography
                  variant="body2"
                  component="h3"
                  className={classes.historyGroup}
                >
                  {event.dateGroup}
                </Typography>
              )}
              <HistoryEventItem event={event} />
            </Fragment>
          ))}
          <div ref={historyLoadMoreRef} style={{ height: 1 }} />
          {isFetchingNextPage && (
            <Box display="flex" justifyContent="center" padding={1}>
              <CircularProgress size={20} />
            </Box>
          )}
        </WbDrawerContent>
      </WbDrawer>
    </Box>
  );
}

/** ─── Main Component ─── */

export function LifecyclePage() {
  const systemLifecycleApi = useApi(systemLifecycleApiRef);
  const { entity } = useEntity();
  const systemUrn = entityToUrn(entity);
  const {
    data: projection,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['system-lifecycle', systemUrn],
    queryFn: () => systemLifecycleApi.getProjection(systemUrn),
  });
  const data = useMemo(
    () => (projection?.available ? toLifecycleData(projection) : undefined),
    [projection],
  );

  if (isLoading) return <Typography>Loading lifecycle...</Typography>;
  if (error) return <Typography>Unable to load lifecycle.</Typography>;
  if (projection?.available && projection.status === 'awaiting-placement') {
    return (
      <Typography>
        This lifecycle is awaiting administrative placement before it can
        process new events.
      </Typography>
    );
  }
  if (
    projection &&
    !projection.available &&
    projection.reason === 'NO_INSTANCE'
  ) {
    return (
      <Typography>
        This System has not been onboarded to the {projection.workflowName}{' '}
        workflow yet.
      </Typography>
    );
  }
  if (!data)
    return <Typography>No lifecycle is active for this System.</Typography>;
  return <LifecycleWorkflowView data={data} systemUrn={systemUrn} />;
}
